/** Seeded, grounded glaciers and their glacial valleys. This is native terrain,
 * not a sea profile or a floating mesh. All dimensions are metres. */
import { hashSeed } from './rng.js';
import * as pmath from './pmath.js';
import { planarField } from './landforms.js';
import type { PlanetDef } from './universe.js';

type V3 = [number, number, number];
export interface GlacierDef {
  /** Coast anchor, inland tangent and cross-valley tangent. */
  origin: V3;
  inland: V3;
  across: V3;
  length: number;
  width: number;
  relief: number;
  seed: number;
}
export interface GlacialSample { heightM: number; iceM: number; weight: number }
const smooth = (v: number): number => { const t = Math.max(0, Math.min(1, v)); return t * t * (3 - 2 * t); };
const norm = (x: number, y: number, z: number): V3 => { const k = 1 / Math.sqrt(x*x+y*y+z*z); return [x*k,y*k,z*k]; };

/** Useful for bookmarks and tests as well as generation. Tangent coordinates
 * are gnomonic, so patches remain continuous across every cube-face boundary. */
export function glacierDirection(g: GlacierDef, radius: number, across: number, inland: number): V3 {
  return norm(g.origin[0]*radius+g.across[0]*across+g.inland[0]*inland,
    g.origin[1]*radius+g.across[1]*across+g.inland[1]*inland,
    g.origin[2]*radius+g.across[2]*across+g.inland[2]*inland);
}

export class GlacialTerrain {
  readonly features: readonly GlacierDef[];
  constructor(private readonly def: PlanetDef, nativeHeight: (x: number,y: number,z: number) => number) {
    if (def.archetype !== 'ice' || def.seaRadius === null || def.terrain.amplitudeRel < .001) {
      this.features = []; return;
    }
    const r=def.radius, sea=def.seaRadius-r, length=Math.min(2400,r*.16);
    const candidates: {g: GlacierDef; score: number}[]=[];
    const phase=(hashSeed(`${def.seed}:glaciers:v1`)>>>0)/4294967296*pmath.TWO_PI;
    // Independent of rivers, settlements, stream order and generation RNG.
    // Reject inland ponds: a mouth needs a descending, submerged seaward side.
    for(let i=0;i<1024;i++) {
      const y=1-2*(i+.5)/1024,a=i*2.399963229728653+phase,q=Math.sqrt(1-y*y);
      const origin:V3=[q*pmath.cos(a),y,q*pmath.sin(a)];
      const h=nativeHeight(...origin)-sea;
      if(h < -30 || h > 70) continue;
      const east=norm(origin[2],0,-origin[0]);
      const north:V3=[origin[1]*east[2],origin[2]*east[0]-origin[0]*east[2],-origin[1]*east[0]];
      const probe=(e:number,n:number) => nativeHeight(...norm(origin[0]+(east[0]*e+north[0]*n)/r,
        origin[1]+north[1]*n/r,origin[2]+(east[2]*e+north[2]*n)/r))-sea;
      const gx=probe(220,0)-probe(-220,0),gy=probe(0,220)-probe(0,-220);
      const gradient=Math.sqrt(gx*gx+gy*gy);
      if(gradient<4)continue;
      const inland=norm(east[0]*gx+north[0]*gy,north[1]*gy,east[2]*gx+north[2]*gy);
      const across:V3=[inland[1]*origin[2]-inland[2]*origin[1],inland[2]*origin[0]-inland[0]*origin[2],inland[0]*origin[1]-inland[1]*origin[0]];
      const g:GlacierDef={origin,inland,across,length,width:length*.29,relief:length*.44,seed:hashSeed(`${def.seed}:glacier:${i}`)};
      const head=nativeHeight(...glacierDirection(g,r,0,length))-sea;
      const offshore=nativeHeight(...glacierDirection(g,r,0,-length*.48))-sea;
      if(head<35 || offshore>-8)continue;
      candidates.push({g,score:Math.min(head,450)-Math.abs(h)*2-Math.max(0,offshore+55)});
    }
    candidates.sort((a,b)=>b.score-a.score);
    const features:GlacierDef[]=[];
    for(const {g} of candidates) {
      if(features.some(other=>{
        const x=other.origin[0]-g.origin[0],y=other.origin[1]-g.origin[1],z=other.origin[2]-g.origin[2];
        return Math.sqrt(x*x+y*y+z*z)*r<length*3.3;
      }))continue;
      features.push(g);if(features.length===3)break;
    }
    this.features=features;
  }

  sample(dx:number,dy:number,dz:number,nativeM:number,out:GlacialSample):GlacialSample {
    out.heightM=nativeM;out.iceM=0;out.weight=0;
    const r=this.def.radius,sea=(this.def.seaRadius??r)-r;
    for(const g of this.features) {
      const dot=dx*g.origin[0]+dy*g.origin[1]+dz*g.origin[2];
      if(dot<.91)continue;
      const x=(dx*g.across[0]+dy*g.across[1]+dz*g.across[2])*r/dot;
      const y=(dx*g.inland[0]+dy*g.inland[1]+dz*g.inland[2])*r/dot;
      const t=y/g.length,ax=Math.abs(x),w=g.width;
      const envelope=smooth((2.6*w-ax)/(.65*w))*smooth((t+.65)/.25)*smooth((1.45-t)/.35);
      if(envelope<=0)continue;
      const bend=planarField(0,t*2,0,g.seed)*w*.18;
      const cross=Math.abs(x-bend),width=w*(.67+.20*smooth(t));
      const wall=smooth((cross-width*.78)/(w*.62));
      // Angular ridge crests enclosing a broad U-shaped trough. Unequal peaks
      // on the two sides keep the silhouette from becoming a paired extrusion.
      const side=x<0?-1:1;
      let crest=0;
      for(let i=0;i<4;i++) {
        const variation=planarField(side*3.7,i*2.8,1.9,g.seed);
        const peakT=-.12+i*.43+variation*.16;
        const peakX=1.44+variation*.25;
        const peakH=.8+variation*.42+(i===2?.23:0);
        crest=Math.max(crest,peakH*Math.max(0,1-Math.abs(t-peakT)/.45-Math.abs(cross/w-peakX)/1.5));
      }
      crest+=.16;
      const headwall=smooth((t-.92)/.32)*.65;
      const rise=Math.max(0,t)*g.relief*.39;
      const bed=sea-38+rise+g.relief*Math.max(wall*crest,headwall)
        +planarField(x/130,y/150,3.2,g.seed)*18*wall;
      // The seaward tongue ends in a finite, steep ramp. Its solid volume runs
      // to bedrock; no floating undercut, caves or breakable ice are implied.
      const fracture=planarField(x/75,y/110,7.3,g.seed);
      const toe=.027+planarField(x/105,1.2,4.8,g.seed)*.016;
      const tongue=smooth((t-toe)/.009)*(1-smooth((t-1.08)/.17));
      const margin=1-smooth((cross-width*.72)/(width*.28));
      const step=.41+planarField(x/180,3.2,2.8,g.seed)*.065;
      const cleft=(1-smooth((Math.abs(fracture)-.02)/.10))*24;
      const thickness=(g.relief*(.13+.07*smooth(t))
        +g.relief*.105*smooth((t-step)/.015)
        +planarField(x/42,y/62,7.3,g.seed)*17-cleft)*tongue*margin;
      const ice=Math.max(0,thickness)*envelope;
      out.heightM=nativeM+(bed-nativeM)*envelope+ice;
      out.iceM=ice;out.weight=envelope;
      return out;
    }
    return out;
  }
}
